// The page's mic and speaker on ONE AudioContext, set up the way the phone page does it: the
// one call other hosts (Skill Builder, a custom page) use so they get the same iOS handling
// rather than their own. See the README, "Mic capture on phones".
//
//   const audio = createPageAudio({ workletUrl: "capture-worklet.js" });
//   const client = new VoiceKitClient({ transport, player: audio.player, capture: audio.capture });
//   audio.capture.on("*", (e) => log("mic_" + e.type, e));
//   audio.capture.on("gesture_needed", () => showTapToResume());
//   button.onclick = () => { client.prepareAudio(); void client.startSession({ audio: "client" }); };

import { AudioHub } from "./audio-hub";
import { MicCapture, standaloneMode, type MicCaptureOptions } from "./capture";
import { WebAudioPlayer, type OutputMode } from "./player";

export interface PageAudioOptions extends Omit<MicCaptureOptions, "hub"> {
  /** How replies reach the speakers (player.ts). Default "direct". */
  output?: OutputMode;
  sinkId?: string;
}

export interface PageAudio {
  hub: AudioHub;
  capture: MicCapture;
  player: WebAudioPlayer;
  /** Running as a home-screen web app (iOS asks for the mic more often there). */
  standalone: boolean;
}

/** Keeps a stopped mic's stream this long by default in a home-screen web app / a tab. */
export const KEEP_STREAM_STANDALONE_MS = 30_000;
export const KEEP_STREAM_TAB_MS = 10_000;

/**
 * A capture and a player sharing one AudioHub. Unless told otherwise, a stopped mic's stream is
 * kept for a short while (longer in a home-screen web app, where iOS prompts on every
 * getUserMedia) so a quick restart neither prompts nor flips the audio route.
 */
export function createPageAudio(opts: PageAudioOptions = {}): PageAudio {
  const hub = new AudioHub();
  const standalone = standaloneMode();
  const { output, sinkId, ...mic } = opts;
  const keepStreamMs = mic.keepStreamMs ?? (standalone ? KEEP_STREAM_STANDALONE_MS : KEEP_STREAM_TAB_MS);
  const capture = new MicCapture({ standalone, ...mic, keepStreamMs, hub });
  const player = new WebAudioPlayer(output ?? "direct", sinkId, { hub });
  return { hub, capture, player, standalone };
}
